/**
 * Company Selector for SuperAdmin — inline sidebar panel
 * Expands inside the sidebar with search and scrollable list.
 */
import React, { useState, useEffect, useRef, useMemo } from 'react';
import { Building2, ChevronDown, Check, Search, Clock, X } from 'lucide-react';
import { useAdminCompanies } from '../../hooks/useCompany';
import { useAuth } from '../../hooks/useAuth';

interface CompanySelectorProps {
  onCompanySelect: (companyId: string, name?: string, tradeName?: string) => void;
  selectedCompanyId?: string;
}

const RECENT_COMPANIES_KEY = 'kessan_recent_companies';
const MAX_RECENT = 5;

export const CompanySelector: React.FC<CompanySelectorProps> = ({
  onCompanySelect,
  selectedCompanyId,
}) => {
  const { user } = useAuth();
  const { data: companies, isLoading } = useAdminCompanies();
  const [isOpen, setIsOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [recentCompanies, setRecentCompanies] = useState<string[]>([]);
  const searchInputRef = useRef<HTMLInputElement>(null);

  if (user?.role !== 'superadmin') return null;

  useEffect(() => {
    try {
      const stored = localStorage.getItem(RECENT_COMPANIES_KEY);
      if (stored) setRecentCompanies(JSON.parse(stored));
    } catch { /* ignore */ }
  }, []);

  useEffect(() => {
    if (isOpen && searchInputRef.current) {
      setTimeout(() => searchInputRef.current?.focus(), 100);
    }
  }, [isOpen]);

  const addToRecent = (companyId: string) => {
    try {
      const updated = [companyId, ...recentCompanies.filter((id) => id !== companyId)].slice(0, MAX_RECENT);
      setRecentCompanies(updated);
      localStorage.setItem(RECENT_COMPANIES_KEY, JSON.stringify(updated));
    } catch { /* ignore */ }
  };

  const filteredCompanies = useMemo(() => {
    if (!companies?.items) return [];
    if (!searchQuery.trim()) return companies.items;
    const query = searchQuery.toLowerCase();
    return companies.items.filter(
      (c) => c.name.toLowerCase().includes(query) || c.companyId.toLowerCase().includes(query)
    );
  }, [companies?.items, searchQuery]);

  const recentCompaniesData = useMemo(() => {
    if (!companies?.items || recentCompanies.length === 0) return [];
    return recentCompanies
      .map((id) => companies.items.find((c) => c.companyId === id))
      .filter((c): c is NonNullable<typeof c> => Boolean(c));
  }, [companies?.items, recentCompanies]);

  const selectedCompany = companies?.items?.find((c) => c.companyId === selectedCompanyId);

  const handleSelect = (companyId: string) => {
    addToRecent(companyId);
    const company = companies?.items?.find((c) => c.companyId === companyId);
    onCompanySelect(companyId, company?.name, company?.tradeName);
    setIsOpen(false);
    setSearchQuery('');
  };

  // Shared company row component
  const CompanyRow = ({ company }: { company: { companyId: string; name: string } }) => {
    const isSelected = selectedCompanyId === company.companyId;
    return (
      <button
        onClick={() => handleSelect(company.companyId)}
        className={`w-full flex items-center gap-2 px-2.5 py-2 text-xs rounded-md transition-colors ${
          isSelected
            ? 'bg-white/25 text-white'
            : 'text-white/90 hover:bg-white/10 hover:text-white'
        }`}
      >
        <Building2 className="w-3.5 h-3.5 flex-shrink-0 opacity-75" />
        <div className="text-left min-w-0 flex-1">
          <div className="font-medium truncate">{company.name}</div>
          <div className="text-[10px] truncate opacity-70">{company.companyId}</div>
        </div>
        {isSelected && <Check className="w-3.5 h-3.5 flex-shrink-0 opacity-80" />}
      </button>
    );
  };

  return (
    <div>
      {/* Toggle button */}
      <button
        onClick={() => setIsOpen(!isOpen)}
        className="flex items-center gap-2 w-full px-3 py-2.5 text-sm font-medium rounded-lg transition-colors bg-white/10 hover:bg-white/15 text-white"
        aria-label="Seleccionar empresa"
        aria-expanded={isOpen}
      >
        <Building2 className="w-4 h-4 flex-shrink-0 opacity-80" />
        <span className="truncate flex-1 text-left text-sm">
          {selectedCompany?.name || 'Seleccionar Empresa'}
        </span>
        <ChevronDown className={`w-4 h-4 flex-shrink-0 opacity-60 transition-transform duration-200 ${isOpen ? 'rotate-180' : ''}`} />
      </button>

      {/* Inline expandable panel */}
      <div
        className={`overflow-hidden transition-all duration-250 ease-in-out ${isOpen ? 'max-h-[360px] opacity-100 mt-2' : 'max-h-0 opacity-0'}`}
      >
        <div className="rounded-lg bg-white/5 border border-white/10">
          {/* Search */}
          <div className="p-2 border-b border-white/10">
            <div className="relative">
              <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-white/50" />
              <input
                ref={searchInputRef}
                type="text"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                placeholder="Buscar empresa..."
                className="w-full pl-8 pr-8 py-1.5 text-xs bg-white/10 border border-white/10 rounded-md text-white placeholder-white/50 focus:outline-none focus:ring-1 focus:ring-white/30 focus:border-white/20"
              />
              {searchQuery && (
                <button
                  onClick={() => { setSearchQuery(''); searchInputRef.current?.focus(); }}
                  className="absolute right-2.5 top-1/2 -translate-y-1/2 text-white/40 hover:text-white/70"
                  aria-label="Limpiar búsqueda"
                >
                  <X className="w-3.5 h-3.5" />
                </button>
              )}
            </div>
          </div>

          {/* Scrollable list */}
          <div className="max-h-[280px] overflow-y-auto p-1.5 scrollbar-thin">
            {isLoading ? (
              <div className="py-6 text-center">
                <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white/50 mx-auto mb-2" />
                <span className="text-xs text-white/50">Cargando...</span>
              </div>
            ) : (
              <>
                {/* Recent */}
                {!searchQuery && recentCompaniesData.length > 0 && (
                  <div className="mb-1">
                    <div className="px-2.5 py-1.5 text-[10px] font-semibold text-white/55 uppercase tracking-wider flex items-center gap-1.5">
                      <Clock className="w-3 h-3" />
                      Recientes
                    </div>
                    {recentCompaniesData.map((c) => <CompanyRow key={c.companyId} company={c} />)}
                    <div className="my-1.5 border-t border-white/10" />
                  </div>
                )}

                {/* All / Filtered */}
                {!searchQuery && (
                  <div className="px-2.5 py-1.5 text-[10px] font-semibold text-white/55 uppercase tracking-wider">
                    Todas las Empresas
                  </div>
                )}
                {filteredCompanies.length > 0 ? (
                  filteredCompanies.map((c) => <CompanyRow key={c.companyId} company={c} />)
                ) : (
                  <div className="py-6 text-center text-xs text-white/50">
                    {searchQuery ? 'No se encontraron empresas' : 'No hay empresas disponibles'}
                  </div>
                )}
              </>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};
